import { children, createEffect, createSignal, JSXElement, onCleanup, onMount, Show, Signal } from "solid-js";
import { Portal, render } from "solid-js/web";
import { Flexbox, Loader } from "st-app";

import style from './st-modal.module.css';
import { dom } from "common";

export class STModalProps {
    show!: Signal<boolean>;
    class?: string;
    title?: string;
    draggable?: boolean;
    children: JSXElement;
}

const emptyImage = new Image();
emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';

export default function STModal(props: STModalProps) {
    const [showModal, setShowModal] = props.show;
    const [offset, setOffset] = createSignal({ x: 0, y: 0 });
    const content = children(() => props.children)

    let dialog: HTMLDivElement;
    let observer: ResizeObserver;
    function initModal(d: HTMLDivElement) {
        dialog = d;
        dialog.addEventListener('close', ev => {
            if (showModal()) {
                setShowModal(false);
            }
        });

        dialog.addEventListener('mousedown', (e) => {
            if (e.target === dialog) {
                setShowModal(false);
            }
        });
        
        requestAnimationFrame(() => {
            let prevWidth = dialog.clientWidth;
            let prevHeight = dialog.clientHeight;

            observer = new ResizeObserver((entries) => {
                const [modalEntry] = entries;

                const currentOffset = offset();

                const newOffsetX = (currentOffset.x / prevWidth) * modalEntry.contentRect.width;
                const newOffsetY = (currentOffset.y / prevHeight) * modalEntry.contentRect.height;
                
                setOffset({ x: newOffsetX, y: newOffsetY });

                prevWidth = modalEntry.contentRect.width;
                prevHeight = modalEntry.contentRect.height;
            })
            observer.observe(dialog);
        })
    }

    let dragStartX = 0;
    let dragStartY = 0;

    const onDragStart = (ev: DragEvent) => {
        ev.dataTransfer?.setDragImage(emptyImage, 0, 0);

        dragStartX = ev.clientX;
        dragStartY = ev.clientY;
    }

    const onDrag = (ev: DragEvent) => {
        const dx = ev.clientX - dragStartX;
        const dy = ev.clientY - dragStartY;

        dragStartX = ev.clientX;
        dragStartY = ev.clientY;

        setOffset({ x: offset().x + dx, y: offset().y + dy });
    }

    onCleanup(() => {
        observer?.disconnect();
    })

    onMount(() => {
        createEffect(() => {
            if (!showModal()) return;
            
            let zIndex = 0;
            let el = dialog?.parentElement as Element | null | undefined;

            while (el = el?.previousElementSibling) {
                if (el instanceof HTMLElement && dom.isElementInViewport(el)) {
                    const z = parseInt(getComputedStyle(el).zIndex);
                    if (z < 10_000 && z > zIndex) zIndex = z;
                }
            }

            dialog?.parentElement?.style.setProperty('z-index', `${zIndex + 1}`);
        })
    })
    
    return <Show when={showModal()}>
        <Portal>
            <div class={style['dialog-container']} ref={initModal}>
                <Flexbox dir="vertical" class={`${style.dialog} ${props.class ?? ''}`} style={{
                    "margin-left": offset().x * 2 + 'px',
                    "margin-top": offset().y * 2 + 'px'
                }}>
                    <Show when={props.title}>
                        <div class={`flexbox horizontal middle padded ${style.header}`}>
                            <h2 class={`auto ${style.title}`} draggable="true" onDragStart={onDragStart} onDrag={onDrag}>{props.title}</h2>
                            <span role="button" class="btn btn-link st-tool-button" onClick={() => setShowModal(false)} aria-label="Close">
                                <i class="fa fa-times" role="presentation"></i>
                            </span>
                        </div>
                    </Show>
                    <Flexbox dir="vertical" auto class={style.content}>
                        {content()}
                    </Flexbox>
                </Flexbox>
            </div>
        </Portal>
    </Show>
}

STModal.defaultProps = new STModalProps();

export async function showDialog<T>(content: (resolve: (value: T) => void, reject: (err: any) => void) => JSXElement, config?: Omit<STModalProps, 'children' | 'show'>): Promise<T | undefined> {
    const [show, setShow] = createSignal(true);
    const container = document.createElement('div');
    document.body.appendChild(container);

    let cleanup: () => void = () => {};

    try {
        let resolved = false;
        const result = await new Promise<T | undefined>((promiseResolve, promiseReject) => {
            createEffect(() => {
                if (!resolved && !show()) {
                    promiseReject();
                }
            });

            const resolve = (value: T | undefined) => {
                resolved = true;
                setShow(false);
                promiseResolve(value);
            }

            const reject = (err: any) => {
                resolved = true;
                setShow(false);
                promiseReject(err);
            }
    
            const dialog = () => (
                <STModal show={[show, setShow]} {...(config ?? {})}>
                    {content(resolve, reject)}
                </STModal>
            )
    
            cleanup = render(dialog, container);
        })

        return result;
    } catch(err) {
        throw err;
    } finally {
        setShow(false);
        cleanup();
        document.body.removeChild(container);
    }
}
